<template>
  <v-app>
    <v-content>
      <v-container class="fill-height" fluid>
        <v-row justify="center" align="center">
          <v-col cols="12" sm="10" md="6" lg="4">
            <v-card>
              <v-card-title>{{ $t("passwordReset") }}</v-card-title>

              <v-card-text>
                <v-alert v-if="errorMessage" type="error" outlined>
                  {{ errorMessage }}
                </v-alert>

                <v-form ref="passwordResetForm" v-model="formValid">
                  <v-text-field
                    v-model="newPassword"
                    :append-icon="showNew ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showNew ? 'text' : 'password'"
                    :label="$t('user.password_new')"
                    :rules="[required, passwordPolicyRule]"
                    outlined
                    @click:append="showNew = !showNew"
                  />

                  <PasswordRequirements
                    v-if="administrativeRole"
                    :value="newPassword"
                  />

                  <v-text-field
                    v-model="passwordConfirm"
                    :append-icon="showConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showConfirm ? 'text' : 'password'"
                    :label="$t('user.password_confirm')"
                    :rules="[required, confirmRule]"
                    outlined
                    @click:append="showConfirm = !showConfirm"
                    @keyup.enter="resetPassword"
                  />
                </v-form>
              </v-card-text>

              <v-divider />

              <v-card-actions>
                <v-btn text :disabled="submitting" @click="goToLogin">
                  {{ $t("back") }}
                </v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  :loading="submitting"
                  :disabled="!canSubmit"
                  @click="resetPassword"
                >
                  {{ $t("apply") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import Axios from "axios";
import config from "@/config";
import PasswordRequirements from "@/components/PasswordRequirements";
import { clearAuthSession } from "@/utils/authSession";
import { isStrongPassword } from "@/utils/passwordPolicy";

export default {
  name: "PasswordReset",
  components: {
    PasswordRequirements,
  },
  data() {
    return {
      token: "",
      administrativeRole: true,
      newPassword: "",
      passwordConfirm: "",
      showNew: false,
      showConfirm: false,
      formValid: false,
      submitting: false,
      errorMessage: "",
    };
  },
  computed: {
    passwordIsValid() {
      return this.administrativeRole
        ? isStrongPassword(this.newPassword)
        : this.newPassword.length >= 4;
    },
    canSubmit() {
      return (
        Boolean(this.token) &&
        this.formValid &&
        this.passwordIsValid &&
        this.newPassword === this.passwordConfirm &&
        !this.submitting
      );
    },
  },
  created() {
    clearAuthSession();

    const hashParameters = new URLSearchParams(
      String(this.$route.hash || "").replace(/^#/, "")
    );
    this.token = hashParameters.get("token") || this.$route.query.token || "";
    const administrativeRole =
      hashParameters.get("administrativeRole") ||
      this.$route.query.administrativeRole;

    if (administrativeRole !== null && administrativeRole !== undefined) {
      this.administrativeRole =
        String(administrativeRole).trim().toLowerCase() === "true";
    }

    if (!this.token) {
      this.errorMessage = this.$i18n.t("invalidPasswordResetLink");
    }
  },
  beforeDestroy() {
    this.clearPasswords();
  },
  methods: {
    required(value) {
      return !!value || this.$i18n.t("user.required");
    },
    passwordPolicyRule(value) {
      if (this.administrativeRole) {
        return (
          isStrongPassword(value) || this.$i18n.t("passwordComplexityError")
        );
      }

      return (
        String(value || "").length >= 4 ||
        this.$i18n.t("password_length_characters", { passLength: 4 })
      );
    },
    confirmRule(value) {
      return (
        value === this.newPassword ||
        this.$i18n.t("user.password_confirm_error")
      );
    },
    clearPasswords() {
      this.newPassword = "";
      this.passwordConfirm = "";
    },
    goToLogin() {
      this.clearPasswords();
      clearAuthSession();
      this.$router.replace({ name: "login" });
    },
    async resetPassword() {
      this.errorMessage = "";
      if (!this.$refs.passwordResetForm.validate() || !this.canSubmit) return;

      this.submitting = true;
      try {
        await Axios.post(
          config.apiBaseUrl + "password-reset",
          {
            token: this.token,
            newPassword: this.newPassword,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        this.clearPasswords();
        clearAuthSession();
        this.$router.replace({
          name: "login",
          query: { passwordReset: "success" },
        });
      } catch (error) {
        const responseData = error.response && error.response.data;
        this.errorMessage =
          (responseData && responseData.message) ||
          responseData ||
          this.$i18n.t("passwordResetError");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
